import { Link } from "@tanstack/react-router";
import { formatPrice, type ShopifyProduct } from "@/lib/shopify";
import aureliaModel from "@/assets/models/aurelia-model.jpg";
import biancaModel from "@/assets/models/bianca-model.jpg";
import camilleModel from "@/assets/models/camille-model.jpg";
import eliseModel from "@/assets/models/elise-model.jpg";
import isabelleModel from "@/assets/models/isabelle-model.jpg";
import margotModel from "@/assets/models/margot-model.jpg";
import siennaModel from "@/assets/models/sienna-model.jpg";
import vivienneModel from "@/assets/models/vivienne-model.jpg";

const MODEL_SHOTS: Record<string, string> = {
  "the-aurelia-layered-honey-blonde-bob": aureliaModel,
  "the-bianca-layered-lace-top-bob": biancaModel,
  "the-camille-chestnut-silk-top-bob": camilleModel,
  "the-elise-darkest-brown-silk-top-12": eliseModel,
  "the-isabelle-wavy-layered-silk-top-14": isabelleModel,
  "the-margot-golden-brown-lace-top-15": margotModel,
  "the-sienna-layered-silk-top-15": siennaModel,
  "the-vivienne-natural-curly-silk-top-12": vivienneModel,
};

function constructionTag(tags: string[]) {
  const cap = tags
    .filter((t) => /^cap:/i.test(t))
    .map((t) => t.replace(/^cap:/i, "").trim());
  if (cap.length) return cap.slice(0, 2).join(" · ");
  const known = ["Lace Front", "Mono Top", "Silk Top", "French Top", "Full Lace"];
  return known.filter((k) => tags.some((t) => t.toLowerCase().includes(k.toLowerCase()))).slice(0, 2).join(" · ") || "Hand-Tied";
}

export function ProductCard({ product }: { product: ShopifyProduct }) {
  const p = product.node;
  const modelShot = MODEL_SHOTS[p.handle];
  const fallback = p.images.edges[0]?.node;
  const imgUrl = modelShot ?? fallback?.url;
  const imgAlt = modelShot ? `Model wearing ${p.title}` : (fallback?.altText ?? p.title);
  const price = p.priceRange.minVariantPrice;
  return (
    <Link
      to="/product/$handle"
      params={{ handle: p.handle }}
      className="group block"
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded bg-muted">
        {imgUrl && (
          <img
            src={imgUrl}
            alt={imgAlt}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        )}
        <span className="absolute left-3 top-3 rounded-full border border-white/50 bg-background/85 px-2.5 py-1 text-[10px] uppercase tracking-widest text-foreground/80 backdrop-blur">
          100% Virgin European Hair
        </span>
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-display text-lg leading-tight">{p.title}</h2>
          <p className="mt-0.5 text-xs uppercase tracking-widest text-muted-foreground">{constructionTag(p.tags)}</p>
        </div>
        <span className="font-display text-lg">{formatPrice(price.amount, price.currencyCode)}</span>
      </div>
    </Link>
  );
}
